<template>
  <base-editor-page id="scenario-page">
    <template v-slot:description>
      <p>
        Create the fictional environment that your participants are entering. This is a great
        opportunity to build engagement with a narrative that simulates a real-world environment.
        The content you provide here is displayed prior to the Inbox experience.
      </p>
    </template>

    <template v-slot:body class="pt-0">
      <!-- Scenario input begin -->
      <div class="form-group">
        <h3 class="mt-0">Describe the Opening Scene</h3>
        <p class="mb-5">
          Content that you provide in this field will be displayed to users immediately before they
          enter the Inbox. This is your final opportunity to tee up the Inbox environment that
          participants are about to dive into. There are countless ways to approach the lead-in – we
          encourage your creativity.
        </p>
        <a
          @click="handleMilestoneNotification"
          style="display: block"
          href="/capsiminbox/resources/CapsimInbox-AP_Situation-Examples.pdf"
          download
        >
          <u> Click here to view examples of opening scene descriptions that authors have used.</u>
        </a>
        <br />
        <template v-if="isOpeningScene">
          <wysiwyg
            @input="handleOpeningSceneSave"
            id="scenario"
            v-model="company.scenario"
            :placeholder="placeholders.scenario"
            :is-skill-or-scenario-page="true"
            :config-overrides="{
              toolbar_location: 'bottom',
              statusbar: true,
              elementpath: false,
            }"
          />
        </template>
        <template v-else>
          <div class="row pl-15 pr-15">
            <div class="col-md-12 inactive-layout pt-10 pb-10 text-center">
              Inactive based on Custom Onboarding settings.
            </div>
          </div>
        </template>
      </div>
      <!-- Scenario input ends -->

      <!-- Role input begins -->
      <div class="form-group">
        <h3>Role Introduction</h3>
        <p class="mb-5">
          This is where you can provide context on the role and scenario that your users will assume
          in the Inbox. This content is especially important if the role differs from their typical
          responsibilities, as they will look at the exercise from a specific perspective. Consider:
          What is their title, where do they work, what are their responsibilities?
        </p>
        <template v-if="isScenario">
          <wysiwyg
            :placeholder="placeholders.role"
            @input="handleRoleTextSave"
            id="role"
            :is-skill-or-scenario-page="true"
            v-model="company.role"
            :config-overrides="{
              toolbar_location: 'bottom',
              statusbar: true,
              elementpath: false,
            }"
          >
          </wysiwyg>
        </template>
        <template v-else>
          <div class="row pl-15 pr-15">
            <div class="col-md-12 inactive-layout pt-10 pb-10 text-center">
              Inactive based on Custom Onboarding settings.
            </div>
          </div>
        </template>
      </div>
      <!-- Role input ends -->

      <!-- Business card begins -->
      <div class="mb-15">
        <h3 style="display: inline-block">Business Card</h3>
        <template v-if="isScenario">
          <v-switch
            class="ml-10"
            style="zoom: 0.9"
            :selected="company.enableBizCard"
            :emit-on-mount="false"
            @input="
              (val) => {
                company.enableBizCard = val;
                handleSave();
              }
            "
            data-testid="switch"
          ></v-switch>
        </template>
        <p>
          If applicable, enable this feature to generate a sample business card that displays next
          to the Introduction you have entered above. These should be specific to the character they
          are assuming in the Inbox.
        </p>

        <template v-if="isScenario">
          <div
            :class="{ disabled: !company.enableBizCard }"
            id="biz-card-inputs"
            class="form-inline"
          >
            <div class="form-group mr-20">
              <label for="job-title">Job Title:</label>
              <input
                :disabled="!company.enableBizCard"
                @input="handleSave"
                v-model="company.jobPosition"
                placeholder="Ex: Managing Director"
                class="form-control"
                id="job-title"
                type="text"
              />
            </div>
            <div class="form-group">
              <label for="company-name">Company Name:</label>
              <input
                :disabled="!company.enableBizCard"
                @input="handleSave"
                v-model="company.companyName"
                placeholder="Ex: Garretts Inc."
                class="form-control"
                type="text"
                id="company-name"
              />
            </div>
          </div>
        </template>
        <template v-else>
          <div class="row pl-15 pr-15">
            <div class="col-md-12 inactive-layout pt-10 pb-10 text-center">
              Inactive based on Custom Onboarding settings.
            </div>
          </div>
        </template>
      </div>
      <!-- Business card ends -->

      <!-- PDF file begin -->
      <div class="form-group">
        <h3>Introductory Material (PDF)</h3>
        <p>
          This feature allows you the ability to upload a PDF that displays directly under the
          Introduction. Some authors use this area to upload an overview of the participant’s
          industry or company – others have designed fake webpages that display key information. The
          utility of the PDF is entirely up to you. If you choose not to upload a PDF, that space
          remains blank.
        </p>
        <template v-if="isScenario">
          <button @click="$refs.fileInputBtn.click()" class="btn btn-default mr-5">
            Choose File
          </button>
          <template v-if="fileName === null">No file chosen</template>
          <template v-else>
            <span class="mr-25"> {{ fileName }} </span>
            <span @click="openFilePreview" class="fas fa-external-link-square-alt mr-5"></span>
            <span @click="deletePdfFile" class="fas fa-trash-alt"></span>
          </template>
          <input
            class="hidden"
            ref="fileInputBtn"
            id="pdfFile"
            @change="handleFileChange($event)"
            type="file"
          />
        </template>
        <template v-else>
          <div class="row pl-15 pr-15">
            <div class="col-md-12 inactive-layout pt-10 pb-10 text-center">
              Inactive based on Custom Onboarding settings.
            </div>
          </div>
        </template>
      </div>
      <!-- PDF file ends -->
    </template>
  </base-editor-page>
</template>
<script>
  import debounce from 'lodash/debounce';
  import { mapActions, mapState, mapGetters } from 'vuex';
  import BaseEditorPage from '@authoringPlatform/shared/BaseEditorPage.vue';
  import Switch from '@shared/switch.vue';
  import cloneDeep from 'lodash/cloneDeep';

  const parseFileName = (fullFileName) => {
    if (!fullFileName) return null;
    const [randomString, fileName] = fullFileName.split('_____');
    return fileName ? fileName : randomString;
  };

  export default {
    name: 'Scenario',
    props: {
      versionKey: {
        type: Number,
        required: true,
      },
    },

    components: {
      BaseEditorPage,
      'v-switch': Switch,
    },

    data() {
      return {
        isInitialized: false,
        fileName: null,
        oldScenarioText: '',
        oldRoleText: '',
        placeholders: {
          role: 'Ex: You are a Managing Director at Garretts Inc., a distribution company based in the US…',
          scenario:
            'Ex: It’s your second week on the job and you’ve just returned after a long weekend…',
        },
        company: {
          roleKey: 0,
          role: '',
          scenario: '',
          pdfFile: null,
          jobPosition: null,
          companyName: null,
          enableBizCard: false,
        },
      };
    },

    watch: {
      'company.scenario': {
        handler(curr, old) {
          if (curr !== old) {
            this.oldScenarioText = old;
          }
        },
      },
      'company.role': {
        handler(curr, old) {
          if (curr !== old) {
            this.oldRoleText = old;
          }
        },
      },
      'role.roleKey': {
        immediate: true,
        handler(roleKey) {
          if (!this.isInitialized && roleKey) {
            this.company = cloneDeep(this.role);
            this.fileName = parseFileName(this.company.pdfFile);
            this.isInitialized = true;
          }
        },
      },
    },

    computed: {
      ...mapState('editMode', ['assetsPath']),
      ...mapState('editMode/version', ['role', 'customPageKeysArray', 'onboardTemplateKey']),
      isScenario() {
        if (this.onboardTemplateKey === 3) {
          if (this.customPageKeysArray.length > 0) {
            for (let obj of this.customPageKeysArray) {
              if (obj.FK_onboardPageKey === 3) {
                return true;
              }
            }
          }

          return false;
        }
        return true;
      },

      isOpeningScene() {
        if (this.onboardTemplateKey === 3) {
          if (this.customPageKeysArray.length > 0) {
            for (let obj of this.customPageKeysArray) {
              if (obj.FK_onboardPageKey === 5) {
                return true;
              }
            }
          }

          return false;
        }
        return true;
      },
    },

    methods: {
      ...mapActions(['SET_MILESTONE']),
      ...mapActions('editMode/version', ['saveRole']),
      ...mapActions('editMode', ['updateSaveStatus']),

      handleMilestoneNotification() {
        this.SET_MILESTONE({
          message: 'View a QuickStart Material',
          tooltipDesc: this.tooltips.milestones['6'],
          timeout: 8000,
          milestoneKey: 6,
        });
      },

      deletePdfFile() {
        $.ajax({
          url: '/capsiminbox/admin/api/roles/delete-pdf-file',
          method: 'DELETE',
          data: JSON.stringify({
            pdfFileName: this.company.pdfFile,
            roleKey: this.company.roleKey,
            versionKey: this.versionKey,
          }),
          dataType: 'json',
          contentType: 'application/json',
        })
          .done((res) => (this.fileName = null))
          .catch((err) => console.log(err));
      },

      handleFileChange(event) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.company.newFile = e.target.result.replace(/^data:application\/\w+;base64,/, '');
          this.fileName = event.target.files[0].name.replace(/ /g, '_');
          this.updateRole().then((role) => {
            this.company.pdfFile = role.pdfFile;
            this.$refs.fileInputBtn.value = null;
          });
        };
        reader.readAsDataURL(event.target.files[0]);
      },

      openFilePreview() {
        window.open(`${this.assetsPath}/capsiminbox/pdfs/${this.company.pdfFile}`, '_blank');
      },

      async updateRole() {
        if (!this.isInitialized) return;
        const payload = {
          ...this.company,
          newFile: this.company.newFile,
          newFileName: this.fileName,
        };

        return await this.saveRole({ payload, roleKey: this.company.roleKey });
      },

      handleOpeningSceneSave() {
        if (this.oldScenarioText !== '' && this.isOpeningScene) {
          this.handleSave();
        }
      },

      handleRoleTextSave() {
        if (this.oldRoleText !== '' && this.isScenario) {
          this.handleSave();
        }
      },

      handleSave() {
        this.updateSaveStatus();
        this.debouncedUpdateRole();
      },

      debouncedUpdateRole: debounce(function () {
        return this.updateRole();
      }, 1000),
    },
  };
</script>
<style lang="scss" scoped>
  #scenario-page {
    .fas {
      cursor: pointer;
    }

    .disabled {
      label {
        color: #8e8e8e;
      }
    }

    h3 {
      font-weight: bold;
      margin-top: 35px;
    }
  }

  .inactive-layout {
    background-color: #dfdfdf;
  }
</style>
